import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

// 30-second axios timeout — enough for slow payment gateway callbacks without hanging the UI forever.
const API_TIMEOUT_MS = 30_000;

export const api = axios.create({
  baseURL: API_BASE,
  timeout: API_TIMEOUT_MS,
});

// Attach tokens if present
api.interceptors.request.use((config) => {
  const admin = localStorage.getItem('pd_admin_token');
  const user = localStorage.getItem('pd_user_token');
  if (admin && config.url && config.url.startsWith('/admin')) {
    config.headers.Authorization = `Bearer ${admin}`;
  } else if (user) {
    config.headers.Authorization = `Bearer ${user}`;
  }
  return config;
});

export const getFeatured = () => api.get('/competitions/featured').then((r) => r.data.competition);
export const getSiteSettings = () => api.get('/site-settings').then((r) => r.data);
export const adminUpdateSiteSettings = (payload) => api.put('/admin/site-settings', payload).then((r) => r.data);
export const listCompetitions = (status) => api.get('/competitions', { params: status ? { status } : {} }).then((r) => r.data);
export const pastDraws = () => api.get('/past-draws').then((r) => r.data);
export const charitySummary = () => api.get('/charity/summary').then((r) => r.data);
export const adminAddCharity = (payload) => api.post('/admin/charity-contributions', payload).then((r) => r.data);
export const adminDeleteCharity = (id) => api.delete(`/admin/charity-contributions/${id}`).then((r) => r.data);
export const getCompetition = (slug) => api.get(`/competitions/${slug}`).then((r) => r.data);
export const listWinners = (category) => api.get('/winners', { params: category && category !== 'all' ? { category } : {} }).then((r) => r.data);
export const getWinner = (id) => api.get(`/winners/${id}`).then((r) => r.data);
export const joinWaitlist = (email, source = 'homepage') => api.post('/waitlist', { email, source }).then((r) => r.data);
export const submitGdpr = (email, reason) => api.post('/gdpr/deletion-request', { email, reason }).then((r) => r.data);
export const submitPostal = (payload) => api.post('/postal-entries', payload).then((r) => r.data);
export const startCheckout = (payload) => api.post('/payments/checkout', payload).then((r) => r.data);
export const getPaymentStatus = (orderRef) => api.get(`/payments/status/${orderRef}`).then((r) => r.data);
export const registerUser = (payload) => api.post('/auth/register', payload).then((r) => r.data);
export const loginUser = (payload) => api.post('/auth/login', payload).then((r) => r.data);
export const requestPasswordReset = (email) => api.post('/auth/forgot-password', { email }).then((r) => r.data);
export const resetPassword = (token, new_password) => api.post('/auth/reset-password', { token, new_password }).then((r) => r.data);
export const unsubscribeInfo = (token) => api.get(`/unsubscribe/${encodeURIComponent(token)}`).then((r) => r.data);
export const unsubscribeApply = (token) => api.post(`/unsubscribe/${encodeURIComponent(token)}`).then((r) => r.data);
export const whoami = () => api.get('/auth/me').then((r) => r.data);
export const myOrders = () => api.get('/account/orders').then((r) => r.data);
export const accountSummary = () => api.get('/account/summary').then((r) => r.data);
export const updateSpendLimit = (payload) => api.post('/account/spend-limit', payload).then((r) => r.data);
export const pauseAccount = (duration) => api.post('/account/pause', { duration }).then((r) => r.data);
export const suspendAccount = () => api.post('/account/suspend', { confirm: true }).then((r) => r.data);
export const closeAccount = () => api.post('/account/close').then((r) => r.data);
export const updateMarketing = (marketing_opt_in) => api.post('/account/marketing', { marketing_opt_in }).then((r) => r.data);
export const isSignedIn = () => Boolean(typeof window !== 'undefined' && localStorage.getItem('pd_user_token'));
export const currentUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('pd_user') || 'null');
  } catch { return null; }
};

// Admin
export const adminLogin = (payload) => api.post('/admin/login', payload).then((r) => r.data);
export const adminDashboard = () => api.get('/admin/dashboard').then((r) => r.data);
export const adminCompetitions = () => api.get('/admin/competitions').then((r) => r.data);
export const adminCreateComp = (payload) => api.post('/admin/competitions', payload).then((r) => r.data);
export const adminUpdateComp = (id, payload) => api.patch(`/admin/competitions/${id}`, payload).then((r) => r.data);
export const adminDeleteComp = (id) => api.delete(`/admin/competitions/${id}`).then((r) => r.data);
export const adminEntries = (id) => api.get(`/admin/entries/${id}`).then((r) => r.data);
export const adminExportEntriesUrl = (id) => `${API_BASE}/admin/entries/${id}/export`;
export const adminSearchUsers = (q) => api.get('/admin/users/search', { params: { q } }).then((r) => r.data);
export const adminGrantEntries = (compId, payload) => api.post(`/admin/competitions/${compId}/grant-entries`, payload).then((r) => r.data);
export const adminListGrantedEntries = (compId) => api.get(`/admin/competitions/${compId}/granted-entries`).then((r) => r.data);
export const adminGetCompetition = (compId) => api.get(`/admin/competitions/${compId}`).then((r) => r.data);
export const adminSetSpendLimit = (userId, spendLimitGbp, reason) => api.post(`/admin/users/${userId}/spend-limit`, { spend_limit_gbp: spendLimitGbp, reason }).then((r) => r.data);
export const adminRunDraw = (id, witness = {}) => api.post('/admin/draw', { competition_id: id, ...witness }).then((r) => r.data);
export const adminPendingWinners = () => api.get('/admin/pending-winners').then((r) => r.data);
export const adminConfirmWinner = (winnerId) => api.post(`/admin/winners/${winnerId}/confirm-contact`).then((r) => r.data);
export const adminRedrawWinner = (winnerId, payload = {}) => api.post(`/admin/winners/${winnerId}/redraw`, payload).then((r) => r.data);
export const adminUpdateWinner = (id, payload) => api.patch(`/admin/winners/${id}`, payload).then((r) => r.data);
export const adminDeleteWinner = (id) => api.delete(`/admin/winners/${id}`).then((r) => r.data);
export const adminWipeWinners = () => api.post('/admin/winners/wipe').then((r) => r.data);
export const adminListDraws = () => api.get('/admin/draws').then((r) => r.data);
export const adminToggleDrawVisibility = (drawId, hidden) => api.post(`/admin/draws/${drawId}/visibility`, { hidden }).then((r) => r.data);
export const adminUpdateDrawWitness = (drawId, payload) => api.patch(`/admin/draws/${drawId}/witness`, payload).then((r) => r.data);
export const adminListPostal = () => api.get('/admin/postal-entries').then((r) => r.data);
export const adminVerifyPostal = (id) => api.post(`/admin/postal-entries/${id}/verify`).then((r) => r.data);
export const adminWaitlist = () => api.get('/admin/waitlist').then((r) => r.data);
export const adminDuplicateFlags = () => api.get('/admin/duplicate-flags').then((r) => r.data);
export const adminResolveDuplicateFlag = (id) => api.post(`/admin/duplicate-flags/${id}/resolve`).then((r) => r.data);
export const adminSpendMonitoring = () => api.get('/admin/spend-monitoring').then((r) => r.data);
export const adminUploadImage = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};
export const adminHarmFlags = () => api.get('/admin/harm-flags').then((r) => r.data);
export const adminResolveHarmFlag = (id) => api.post(`/admin/harm-flags/${encodeURIComponent(id)}/resolve`).then((r) => r.data);

// --- Customers (admin) ---
export const adminListUsers = (params = {}) => api.get('/admin/users', { params }).then((r) => r.data);
export const adminGetUser = (id) => api.get(`/admin/users/${encodeURIComponent(id)}`).then((r) => r.data);
export const adminPauseUser = (id, reason = '') => api.post(`/admin/users/${encodeURIComponent(id)}/pause`, { reason }).then((r) => r.data);
export const adminSuspendUser = (id, reason = '') => api.post(`/admin/users/${encodeURIComponent(id)}/suspend`, { reason }).then((r) => r.data);
export const adminReactivateUser = (id) => api.post(`/admin/users/${encodeURIComponent(id)}/reactivate`).then((r) => r.data);
export const adminSendResetToUser = (id) => api.post(`/admin/users/${encodeURIComponent(id)}/send-reset`).then((r) => r.data);
export const adminUsersCsvUrl = (search = '') => {
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  return `${API_BASE}/admin/users.csv${qs}`;
};

// --- Broadcasts (admin) ---
export const adminBroadcastPreview = (audience) => api.post('/admin/broadcasts/preview', { audience }).then((r) => r.data);
export const adminBroadcastTest = (payload) => api.post('/admin/broadcasts/test', payload).then((r) => r.data);
export const adminBroadcastSend = (payload) => api.post('/admin/broadcasts/send', payload).then((r) => r.data);
export const adminListBroadcasts = () => api.get('/admin/broadcasts').then((r) => r.data);


// --- Partner-code batches ("Got a code?" single-use) ---
export const redeemPartnerCode = (code) =>
  api.post('/partner-codes/redeem', { code }).then((r) => r.data);

export const adminListPartnerCodeBatches = () =>
  api.get('/admin/partner-code-batches').then((r) => r.data);
export const adminCreatePartnerCodeBatch = (payload) =>
  api.post('/admin/partner-code-batches', payload).then((r) => r.data);
export const adminGetPartnerCodeBatch = (id) =>
  api.get(`/admin/partner-code-batches/${id}`).then((r) => r.data);
export const adminUpdatePartnerCodeBatch = (id, payload) =>
  api.patch(`/admin/partner-code-batches/${id}`, payload).then((r) => r.data);
export const adminListBatchCodes = (id, params = {}) =>
  api.get(`/admin/partner-code-batches/${id}/codes`, { params }).then((r) => r.data);
export const adminBatchVoidRemaining = (id) =>
  api.post(`/admin/partner-code-batches/${id}/void-remaining`).then((r) => r.data);
export const adminBatchSendTest = (id, payload) =>
  api.post(`/admin/partner-code-batches/${id}/send-test`, payload).then((r) => r.data);
export const adminBatchAssignAndSend = (id, payload) =>
  api.post(`/admin/partner-code-batches/${id}/assign-and-send`, payload).then((r) => r.data);
export const adminBatchRetrySend = (batchId, codeId) =>
  api.post(`/admin/partner-code-batches/${batchId}/codes/${codeId}/retry-send`).then((r) => r.data);
export const adminDeletePartnerCodeBatch = (id) =>
  api.delete(`/admin/partner-code-batches/${id}`).then((r) => r.data);
